import React, { useEffect, useRef, useState } from "react";
import { displayWarningModal } from "@dealer-center/inventory-core-utils";
import { WarningModal } from "./WarningModal";
import {
  useGetTrimMapWithDetails,
  useGetVehicleWithDetails,
  useNavigateToLiveQueryPage
} from "@dealer-center/inventory-pages-gvm-fetch";
import type {
  TrimViewFormData,
  VehicleViewFormData
} from "@dealer-center/inventory-pages-gvm-interface";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Alert } from "@dealer-center/core-components";
import { SearchView } from "./SearchView";
import { VehicleView } from "./VehicleView";
import { TrimView } from "./TrimView";

interface LiveQueryPageProps {
  editVehicle?: VehicleViewFormData;
  editTrim?: TrimViewFormData;
}

export const LiveQueryPage = ({
  editVehicle,
  editTrim
}: LiveQueryPageProps) => {
  const firstLoad = useRef(true);
  const [isShowWarningModal, setIsShowWarningModal] = useState(false);

  // Edit vehicle
  const [vehicleViewFormData, setVehicleViewFormData] =
    useState<VehicleViewFormData>(
      () => editVehicle ?? { editingVehicle: false }
    );
  const vehicleWithDetailsQuery = useGetVehicleWithDetails(vehicleViewFormData);

  // Edit trim
  const [trimViewFormData, setTrimViewFormData] = useState<TrimViewFormData>(
    () => editTrim ?? { editingTrim: false }
  );
  const trimWithDetailsQuery = useGetTrimMapWithDetails(trimViewFormData);

  const goToLiveQueryPage = useNavigateToLiveQueryPage();

  useEffect(() => {
    if (firstLoad.current) {
      setIsShowWarningModal(displayWarningModal());
      firstLoad.current = false;
    }
  }, [firstLoad]);

  const renderWarningModal = () => (
    <WarningModal
      show={isShowWarningModal}
      onHide={() => setIsShowWarningModal(false)}
    />
  );

  const renderLoadingData = () => (
    <div
      style={{
        marginTop: "75px",
        padding: "0px",
        marginLeft: "10px",
        marginRight: "10px"
      }}
    >
      <div
        style={{
          border: "1px #DDD solid",
          margin: "0px",
          padding: "8px",
          width: "100%",
          textAlign: "center",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <LoadingIndicator actionName="Loading data" text="Loading" />
      </div>
    </div>
  );

  const renderQueryError = (error: Error) => (
    <Alert role="alert" type="error">
      {error.message}
    </Alert>
  );

  const render = () => {
    if (isShowWarningModal) {
      return renderWarningModal();
    }

    if (vehicleWithDetailsQuery.isLoading || trimWithDetailsQuery.isLoading) {
      return renderLoadingData();
    }

    const queryError =
      vehicleWithDetailsQuery.error ?? trimWithDetailsQuery.error;
    if (queryError) {
      return renderQueryError(queryError);
    }

    if (!vehicleViewFormData.editingVehicle && !trimViewFormData.editingTrim) {
      return <SearchView renderLoadingData={renderLoadingData} />;
    }

    if (vehicleViewFormData.editingVehicle) {
      return (
        <VehicleView
          closeVehicleView={goToLiveQueryPage}
          locale={vehicleViewFormData.locale ?? ""}
          vehicleId={vehicleViewFormData.vehicleId ?? ""}
          vehicleWithDetailsQuery={vehicleWithDetailsQuery}
        />
      );
    }

    if (trimViewFormData.editingTrim) {
      return (
        <TrimView
          closeTrimView={goToLiveQueryPage}
          locale={trimViewFormData.locale ?? ""}
          trimId={trimViewFormData.trimId ?? ""}
          trimWithDetailsQuery={trimWithDetailsQuery}
        />
      );
    }

    return null;
  };

  return render();
};
